import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { purchases as purchasesApi, collections as collectionsApi, progress as progressApi } from '../api';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [myCollections, setMyCollections] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [purchaseData, collData] = await Promise.all([
          purchasesApi.myCollections(),
          collectionsApi.getAll()
        ]);
        const purchased = purchaseData.collections.map(p => {
          const coll = collData.collections.find(c => c.collectionId === p.collectionId);
          return coll ? { ...coll, purchaseDate: p.purchaseDate } : null;
        }).filter((c, i, arr) => c && arr.findIndex(x => x.collectionId === c.collectionId) === i);
        setMyCollections(purchased);

        const progEntries = await Promise.all(
          purchased.map(c => progressApi.get(c.collectionId).then(d => [c.collectionId, d]).catch(() => [c.collectionId, null]))
        );
        setProgressMap(Object.fromEntries(progEntries));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const readinessColor = (pct) => pct >= 80 ? '#22c55e' : pct >= 60 ? '#eab308' : '#ef4444';

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <nav className="nav">
        <div className="nav-content">
          <Link to="/" className="logo">C3</Link>
          <div className="nav-links">
            <span className="user-email">{user?.email}</span>
            <button onClick={handleLogout} className="btn-secondary">Logout</button>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>My Collections</h1>
          <Link to="/browse" className="btn-primary">Browse Collections</Link>
        </div>

        {myCollections.length > 0 ? (
          <div className="collections-grid">
            {myCollections.map(coll => {
              const prog = progressMap[coll.collectionId];
              const studied = prog?.studiedCards || 0;
              const total = prog?.totalCards || coll.totalCards;
              const pct = total ? Math.round((studied / total) * 100) : 0;
              const avg = prog?.averageRating || 0;
              const readiness = total && avg ? Math.round((studied / total) * Math.max(0, (avg - 1) / 4) * 100) : 0;

              return (
                <div key={coll.collectionId} className="collection-item">
                  <h3>{coll.title}</h3>
                  <div className="collection-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="progress-text">{studied} / {total} cards studied ({pct}%)</p>
                  </div>
                  <div className="readiness-badge" style={{ background: readinessColor(readiness) }}>
                    {readiness}% exam ready {avg > 0 && `· avg ${avg}/5`}
                  </div>
                  <div className="collection-actions">
                    {[5, 10, 15].map(n => (
                      <Link key={n} to={`/study/${coll.collectionId}?size=${n}`} className="btn-primary btn-sm">Study {n}</Link>
                    ))}
                    <Link to={`/progress/${coll.collectionId}`} className="btn-secondary btn-sm">Progress</Link>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <p>Purchase a collection to start studying.</p>
            <Link to="/browse" className="btn-primary">Browse Collections</Link>
          </div>
        )}
      </div>
    </div>
  );
};


export default Dashboard;
